import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import {
  DOC_CATEGORY,
  DOC_STATUS,
  DOC_TYPE,
  metaFor,
  type ComplianceDocument,
} from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const options = (list: string[]) => list.map((value) => ({ value, label: value }));

const columns: Column<ComplianceDocument>[] = [
  {
    key: "title",
    header: "Document",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.title}</p>
        <p className="text-xs text-foreground-500 mt-0.5">
          {row.doc_type} · {row.related_to || "—"}
        </p>
      </div>
    ),
  },
  { key: "category", header: "Category", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.category}</span> },
  { key: "issued", header: "Issued", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.issued_at)}</span> },
  { key: "expires", header: "Expires", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.expires_at)}</span> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(DOC_STATUS, row.status)} /> },
];

const config: ResourceModuleConfig<ComplianceDocument> = {
  table: "compliance_documents",
  orderBy: "expires_at",
  eyebrow: "Safety & Compliance",
  title: "Documents",
  subtitle: "Registrations, licences, insurance and certificates for the business and the farm network — with expiry tracking.",
  searchPlaceholder: "Search documents, issuers…",
  searchKeys: ["title", "doc_type", "category", "related_to", "issued_by"],
  filters: [
    { key: "status", label: "Status", options: optionsFrom(DOC_STATUS) },
    { key: "category", label: "Category", options: options(DOC_CATEGORY) },
  ],
  statusMap: DOC_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Documents", value: rows.length, icon: "ri-folder-shield-2-line", tone: "primary" },
    { label: "Valid", value: rows.filter((row) => row.status === "valid").length, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "Expiring soon", value: rows.filter((row) => row.status === "expiring").length, icon: "ri-timer-line", tone: "accent" },
    { label: "Expired", value: rows.filter((row) => row.status === "expired").length, icon: "ri-close-circle-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "title", label: "Title" },
    { key: "doc_type", label: "Document type", type: "select", options: options(DOC_TYPE) },
    { key: "category", label: "Category", type: "select", options: options(DOC_CATEGORY) },
    { key: "related_to", label: "Relates to" },
    { key: "issued_by", label: "Issued by" },
    { key: "issued_at", label: "Issued on", type: "date" },
    { key: "expires_at", label: "Expires on", type: "date" },
    { key: "status", label: "Status", type: "select", options: optionsFrom(DOC_STATUS) },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  createFields: [
    { key: "title", label: "Title" },
    { key: "doc_type", label: "Document type", type: "select", options: options(DOC_TYPE) },
    { key: "category", label: "Category", type: "select", options: options(DOC_CATEGORY) },
    { key: "related_to", label: "Relates to" },
    { key: "issued_by", label: "Issued by" },
    { key: "issued_at", label: "Issued on", type: "date" },
    { key: "expires_at", label: "Expires on", type: "date" },
  ],
  newDefaults: {
    title: "",
    doc_type: "Certificate",
    category: "Legal",
    related_to: "",
    issued_by: "",
    issued_at: null,
    expires_at: null,
    status: "valid",
    notes: "",
  },
  emptyTitle: "No documents yet",
  emptyMessage: "Upload or record the licences, insurance and certificates that keep you compliant.",
  rowTitle: (row) => row.title,
};

export default function AdminDocuments() {
  return <ResourceModule config={config} />;
}